import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authResendVerification } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resending, setResending] = useState(false);
  // 2FA-Schritt
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setNeedsVerify(false);
    try {
      const u = await login(email.trim(), password);
      if (u?.two_factor_required) {
        setChallengeId(u.challenge_id);
        setBusy(false);
        return;
      }
      navigate(u?.role === "admin" ? "/administration" : "/");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setNeedsVerify(true);
        setError(detail.message || "Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse.");
      } else {
        setError(typeof detail === "string" ? detail : "Anmeldung fehlgeschlagen.");
      }
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await verifyTwoFactor(challengeId, code.trim());
      navigate(u?.role === "admin" ? "/administration" : "/");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Code ungültig oder abgelaufen.");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await authResendVerification(email.trim());
      setError("Bestätigungs-E-Mail wurde erneut gesendet. Bitte prüfen Sie Ihr Postfach.");
      setNeedsVerify(false);
    } catch {
      setError("Senden fehlgeschlagen. Bitte später erneut versuchen.");
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShell
      title="Anmelden"
      subtitle="Willkommen zurück bei BeSca."
      footer={
        <span>
          Noch kein Konto?{" "}
          <Link to="/registrieren" className="text-primary font-medium hover:underline" data-testid="link-register">
            Jetzt registrieren
          </Link>
        </span>
      }
    >
      {error && (
        <div
          className="mb-4 border-l-2 border-destructive bg-destructive/10 px-3 py-3 text-sm text-destructive"
          data-testid="login-error"
        >
          {error}
          {needsVerify && (
            <Button
              type="button"
              variant="secondary"
              className="mt-3 w-full border border-border"
              onClick={resend}
              disabled={resending}
              data-testid="login-resend-verification"
            >
              {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Bestätigungs-E-Mail erneut senden"}
            </Button>
          )}
        </div>
      )}
      {challengeId ? (
        <form onSubmit={verify} className="space-y-4" data-testid="twofa-form">
          <p className="text-sm text-muted-foreground">
            Wir haben Ihnen einen 6-stelligen Anmeldecode per E-Mail gesendet. Bitte geben Sie ihn ein.
          </p>
          <div className="space-y-2">
            <Label htmlFor="twofa-code">Anmeldecode</Label>
            <Input
              id="twofa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required
              data-testid="twofa-code"
            />
          </div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="twofa-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Bestätigen"}
          </Button>
        </form>
      ) : (
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">E-Mail</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            data-testid="login-email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Passwort</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            data-testid="login-password"
          />
        </div>
        <div className="flex justify-end">
          <Link to="/passwort-vergessen" className="text-sm text-muted-foreground hover:text-primary" data-testid="link-forgot">
            Passwort vergessen?
          </Link>
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anmelden"}
        </Button>
      </form>
      )}
    </AuthShell>
  );
}
